import qs from 'qs';
import auth from '~/auth';

import { GridPaginationModel } from '@mui/x-data-grid';

import { BusinessResponses, paths } from '@luxuryescapes/contract-business';

import { FrequentFlyerItemType } from '~/components/LeBusinessTraveller/pages/BusinessDetailsPage/Tabs/FrequentFlyerTab';

import { json_headers, request } from '~/services/common';

export function businessTravellerBasePath() {
  return window.configs.API_HOST + '/api/business-traveller';
}
interface OkResponse<T> {
  status: number;
  meta?: {
    limit: number;
    page: number;
    total: number;
  };
  result: Array<T>;
}

interface Params {
  pageSize: number;
  page: number;
}

// BUSINESS REQUESTS
export async function getBusiness(id: string): Promise<App.Business> {
  const url = `${businessTravellerBasePath()}/businesses/${id}`;
  const response: BusinessResponses.BusinessIndex = await request(url, { method: 'GET' });
  return response.result;
}

export async function getBusinessList(
  limit: number,
  page: number,
  nameString?: string,
  businessId?: string,
  status?: 'active' | 'deactivated',
): Promise<OkResponse<App.Business>> {
  const queryStrings = businessId
    ? { limit, page, businessId, status }
    : { limit, page, businessName: nameString, status };
  const url = `${businessTravellerBasePath()}/businesses?${qs.stringify(queryStrings)}`;
  const response: BusinessResponses.BusinessList = await request(url, { method: 'GET' });
  return response;
}

type BusinessPatch = Omit<App.Business, 'id' | 'email'>;

export async function patchBusiness(id: string, business: App.Business): Promise<void> {
  const url = `${businessTravellerBasePath()}/businesses/${id}`;

  const businessPatch: BusinessPatch = {
    businessIdentifier: business.businessIdentifier,
    country: business.country,
    industry: business.industry,
    name: business.name,
    phone: business.phone,
    employeeCount: business.employeeCount,
    enableMerchantFees: business.enableMerchantFees,
  };

  return request(url, {
    body: JSON.stringify(businessPatch),
    headers: json_headers,
    method: 'PATCH',
  });
}

type DeactivateBusinessResponse =
  paths['/api/business-traveller/businesses/{businessId}/deactivate']['post']['responses'][200]['content']['application/json'];

export async function deactivateBusiness(
  businessId: string,
  payload: { preview: boolean; reason: string },
): Promise<DeactivateBusinessResponse> {
  const url = `${businessTravellerBasePath()}/businesses/${businessId}/deactivate`;
  return await request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(payload),
  });
}

/**
 * Business Settings Request Types
 */
type BusinessSettingSetOperation = paths['/api/business-traveller/business-settings']['put'];

// Request body shape for POST / PUT.
export type BusinessSettings = BusinessSettingSetOperation['requestBody']['content']['application/json']['settings'];

// Response shape from GET / PUT / POST. Each setting key is wrapped with its DB row id under `id`,
// with the domain object under `value`.
export type BusinessSettingsResponse =
  BusinessSettingSetOperation['responses'][200]['content']['application/json']['result'];

export type BusinessSettingName = keyof BusinessSettings;
export type Societe = NonNullable<BusinessSettings['societe']>;
export type SocieteTier = keyof Societe;

type BusinessSettingsApiResponse = BusinessSettingSetOperation['responses'][200]['content']['application/json'];

/**
 * Business Settings Request Functions
 */
function businessSettingsBasePath() {
  return window.configs.API_HOST + '/api/business-traveller/business-settings';
}

// Omit `businessId` to fetch the global default settings.
export async function getBusinessSettings(businessId?: string): Promise<BusinessSettingsResponse> {
  const url = businessId ? `${businessSettingsBasePath()}?${qs.stringify({ businessId })}` : businessSettingsBasePath();
  const response: BusinessSettingsApiResponse = await request(url, { method: 'GET' });
  return response.result;
}

// Upsert business-specific settings. Creates a row if none exists for the (businessId, settingName) pair,
// otherwise replaces the existing row.
export async function upsertBusinessSettings(payload: {
  businessId: string;
  settings: BusinessSettings;
}): Promise<BusinessSettingsResponse> {
  const response: BusinessSettingsApiResponse = await request(businessSettingsBasePath(), {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(payload),
  });
  return response.result;
}

// Create the global default settings row.
export async function createDefaultBusinessSettings(settings: BusinessSettings): Promise<BusinessSettingsResponse> {
  const url = `${businessSettingsBasePath()}/defaults`;
  const response: BusinessSettingsApiResponse = await request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify({ settings }),
  });
  return response.result;
}

// Update an existing default settings row by id.
export async function updateDefaultBusinessSettings(
  id: string,
  settings: BusinessSettings,
): Promise<BusinessSettingsResponse> {
  const url = `${businessSettingsBasePath()}/defaults/${id}`;
  const response: BusinessSettingsApiResponse = await request(url, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify({ settings }),
  });
  return response.result;
}

// Delete a business-specific setting row by id. The business falls back to the global default.
export async function deleteBusinessSetting(id: string): Promise<void> {
  const url = `${businessSettingsBasePath()}/${id}`;
  await request(url, { method: 'DELETE', headers: json_headers });
}

// EMPLOYEE REQUESTS
export async function getEmployee(customerId: string): Promise<App.BusinessEmployee> {
  const url = `${businessTravellerBasePath()}/employees?${qs.stringify({ customerId })}`;
  const response: BusinessResponses.EmployeeIndex = await request(url, { method: 'GET' });
  return response.result;
}

export async function getEmployeeList(businessId: string, params: Params): Promise<OkResponse<App.BusinessEmployee>> {
  const limit = params.pageSize;
  const page = params.page;

  const url = `${businessTravellerBasePath()}/businesses/${businessId}/employees?${qs.stringify({
    limit,
    page,
  })}`;

  const response: BusinessResponses.EmployeeListIndex = await request(url, { method: 'GET' });
  return response;
}

export async function getEmployeeSearch(
  businessId: string,
  searchString: string,
  params: Params,
): Promise<OkResponse<App.BusinessEmployee>> {
  const limit = params.pageSize;
  const page = params.page;

  const url = `${businessTravellerBasePath()}/businesses/${businessId}/employees?${qs.stringify({
    searchQuery: searchString,
    limit,
    page,
  })}`;

  const response: BusinessResponses.EmployeeListIndex = await request(url, { method: 'GET' });
  return response;
}

export async function batchFetchEmployees(
  businessId: string,
  employeeIds: Array<string>,
): Promise<Array<App.BusinessEmployee>> {
  const url = `${businessTravellerBasePath()}/businesses/${businessId}/employees/batch-fetch`;

  const payload = {
    employeeIds,
  };

  const response = await request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(payload),
  });

  return response.result || [];
}

export async function getBusinessAdminSpoofingLink(params: { customerId: string }): Promise<string> {
  const accessToken = await auth.getAccessToken({
    impersonation: {
      customerId: params.customerId,
    },
  });

  if (!accessToken) {
    throw new Error('You are not logged in.');
  }

  const apiUrl = new URL('/redirect', window.configs.LEBUSINESSTRAVELLER_API_DOMAIN);
  apiUrl.searchParams.set('url', window.configs.LEBUSINESSTRAVELLER_ADMIN_PORTAL);
  apiUrl.searchParams.set('token', accessToken);

  return apiUrl.toString();
}

// ORDER ITEM CREDIT REQUESTS
export async function getOrderCreditItem(orderId, orderItemId: string): Promise<BusinessResponses.OrderItemFetch> {
  const url = `${businessTravellerBasePath()}/order/${orderId}/order-items?orderItemId=${orderItemId}`;
  return await request(url, { method: 'GET' });
}

export type OrderItemV2Response =
  paths['/api/business-traveller/v2/order-items/{externalOrderItemId}']['get']['responses'][200]['content']['application/json'];

export type OrderItemV2 = OrderItemV2Response['result'];

export type OrderItemV2Traveller = NonNullable<OrderItemV2['travellers']>[number];

export async function getOrderItemV2(orderItemId: string): Promise<OrderItemV2Response> {
  const url = `${businessTravellerBasePath()}/v2/order-items/${orderItemId}?includeTravellers=true`;
  return await request(url, { method: 'GET' });
}

type ApplyOrderItemTravellerCreditResponse =
  paths['/api/business-traveller/order-item-travellers/{orderItemTravellerId}/apply-credit']['post']['responses'][200]['content']['application/json'];

export async function applyOrderItemTravellerCredit(
  orderItemTravellerId: string,
): Promise<ApplyOrderItemTravellerCreditResponse> {
  const url = `${businessTravellerBasePath()}/order-item-travellers/${orderItemTravellerId}/apply-credit`;
  return await request(url, {
    method: 'POST',
    headers: json_headers,
  });
}

export async function applyOrderCreditItem(orderItemId: string): Promise<BusinessResponses.ItemCreditApplied> {
  const url = `${businessTravellerBasePath()}/order-items/apply-credit`;
  return await request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify({ orderItemId: orderItemId }),
  });
}

export async function getFrequentFlyerList(businessId: string) {
  const url = `${businessTravellerBasePath()}/businesses/${businessId}/frequent-flyer`;

  const response: BusinessResponses.FrequentFlyerListIndex = await request(url, { method: 'GET' });
  return response;
}

export async function createFrequentFlyer(frequentFlyerList: Array<FrequentFlyerItemType>, businessId: string) {
  const url = `${businessTravellerBasePath()}/businesses/${businessId}/frequent-flyer`;

  return await request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(frequentFlyerList),
  });
}

export async function patchFrequentFlyerList(frequentFlyerList: Array<FrequentFlyerItemType>, businessId: string) {
  const url = `${businessTravellerBasePath()}/businesses/${businessId}/frequent-flyer`;

  return await request(url, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(frequentFlyerList),
  });
}

export async function deleteFrequentFlyer(frequentFlyerId: string, businessId: string) {
  const url = `${businessTravellerBasePath()}/businesses/${businessId}/frequent-flyer/${frequentFlyerId}`;

  return await request(url, {
    method: 'DELETE',
    headers: json_headers,
  });
}

// SERVICE FEE REQUESTS

export async function getServiceFee(businessId: string, serviceFeeId: string): Promise<App.BusinessServiceFee> {
  const url = `${businessTravellerBasePath()}/businesses/${businessId}/service-fees/${serviceFeeId}`;
  const response = await request(url, { method: 'GET' });
  return response.result;
}

export async function getServiceFeeList(
  businessId: string,
  paginationModel?: GridPaginationModel,
): Promise<OkResponse<App.BusinessServiceFee>> {
  const queryStrings = {
    businessId,
    limit: paginationModel?.pageSize,
    page: paginationModel?.page,
  };
  const url = `${businessTravellerBasePath()}/businesses/${businessId}/service-fees?${qs.stringify(queryStrings)}`;
  const response = await request(url, { method: 'GET' });
  return response;
}

type BusinessServiceFeePayload = Omit<App.BusinessServiceFee, 'id' | 'businessId'>;

export async function createServiceFee(serviceFee: BusinessServiceFeePayload, businessId: string) {
  const url = `${businessTravellerBasePath()}/businesses/${businessId}/service-fees`;

  return await request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(serviceFee),
  });
}

export async function patchServiceFee(serviceFee: BusinessServiceFeePayload, businessId: string, serviceFeeId: string) {
  const url = `${businessTravellerBasePath()}/businesses/${businessId}/service-fees/${serviceFeeId}`;

  return await request(url, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(serviceFee),
  });
}

export async function deleteServiceFee(businessId: string, serviceFeeId: string) {
  const url = `${businessTravellerBasePath()}/businesses/${businessId}/service-fees/${serviceFeeId}`;

  return await request(url, {
    method: 'DELETE',
    headers: json_headers,
  });
}

export async function resetServiceFees(businessId: string) {
  const url = `${businessTravellerBasePath()}/businesses/${businessId}/service-fees/reset`;

  return await request(url, {
    method: 'POST',
    headers: json_headers,
  });
}

// EMPLOYEE LOYALTY PROGRAMS REQUESTS

export interface EmployeeLoyaltyPrograms {
  program: string;
  memberNumber: string;
  type: string;
}

type EmployeeLoyaltyProgramsResponse =
  paths['/api/business-traveller/businesses/{businessId}/employee-loyalty-programs']['put']['responses'][200]['content']['application/json'];
export type EmployeeLoyaltyProgramsRequest =
  paths['/api/business-traveller/businesses/{businessId}/employee-loyalty-programs']['put']['requestBody']['content']['application/json'];

type EmployeeLoyaltyProgramsGetResponse =
  paths['/api/business-traveller/businesses/{businessId}/employees/{customerId}/loyalty-programs']['get']['responses'][200]['content']['application/json'];

export async function getEmployeeLoyaltyPrograms(
  businessId: string,
  customerId: string,
): Promise<EmployeeLoyaltyProgramsGetResponse> {
  const url = `${businessTravellerBasePath()}/businesses/${businessId}/employees/${customerId}/loyalty-programs`;

  return await request(url, {
    method: 'GET',
  });
}

export async function updateEmployeeLoyaltyPrograms(
  businessId: string,
  payload: EmployeeLoyaltyProgramsRequest,
): Promise<EmployeeLoyaltyProgramsResponse> {
  const url = `${businessTravellerBasePath()}/businesses/${businessId}/employee-loyalty-programs?without_brand=1`;

  return await request(url, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(payload),
  });
}

// ACTIVITY LOG
export async function getActivityLog(
  businessId: string,
  paginationModel: GridPaginationModel,
  options?: {
    searchQuery?: string;
    sort?: string;
    sortDirection?: string;
    resource?: string;
    operation?: string;
  },
): Promise<OkResponse<App.BusinessActivityLog>> {
  const { page, pageSize: limit } = paginationModel;
  const queryStrings = {
    limit,
    page,
    ...(options?.searchQuery && { searchQuery: options.searchQuery }),
    ...(options?.sort && { sort: options.sort }),
    ...(options?.sortDirection && { sortDirection: options.sortDirection }),
    ...(options?.resource && { resource: options.resource }),
    ...(options?.operation && { operation: options.operation }),
  };
  const url = `${businessTravellerBasePath()}/businesses/${businessId}/activity?${qs.stringify(queryStrings)}`;
  const response = await request(url, { method: 'GET' });
  return response;
}

// Order Sync
export async function syncOrder(orderId: string) {
  const url = `${businessTravellerBasePath()}/orders-sync/sync?orderId=${orderId}`;

  return await request(url, {
    method: 'POST',
    headers: json_headers,
  });
}

export async function syncBusiness(businessId: string) {
  const url = `${businessTravellerBasePath()}/orders-sync/sync?businessId=${businessId}`;

  return await request(url, {
    method: 'POST',
    headers: json_headers,
  });
}

export async function syncAllBusinesses() {
  const url = `${businessTravellerBasePath()}/orders-sync/sync-all`;

  return await request(url, {
    method: 'POST',
    headers: json_headers,
  });
}

export type BusinessOrder =
  paths['/api/business-traveller/businesses/{businessId}/orders']['post']['requestBody']['content']['application/json'];

export async function createBusinessOrder(businessId: string, order: BusinessOrder) {
  if (!businessId) {
    throw new Error('Business ID is required to place this order with LEBT');
  }

  const url = `${businessTravellerBasePath()}/businesses/${businessId}/orders-admin`;

  return await request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(order),
  });
}

// get merged business order data (merged svc-business + svc-order data)
export type BusinessOrderGetResponse =
  paths['/api/business-traveller/le-admin/businesses/{businessId}/orders/{externalOrderId}']['get']['responses'][200]['content']['application/json'];

export async function getBusinessOrder(businessId: string, externalOrderId: string): Promise<BusinessOrderGetResponse> {
  const url = `${businessTravellerBasePath()}/le-admin/businesses/${businessId}/orders/${externalOrderId}`;
  return await request(url, { method: 'GET' });
}

// Resend booking confirmation email
export type ResendBookingConfirmationPayload =
  paths['/api/business-traveller/le-admin/businesses/{businessId}/orders/{externalOrderId}/resend-booking-confirmation']['post']['requestBody']['content']['application/json'];

export async function resendLeAdminBookingConfirmation(
  businessId: string,
  externalOrderId: string,
  payload: ResendBookingConfirmationPayload,
) {
  const url = `${businessTravellerBasePath()}/le-admin/businesses/${businessId}/orders/${externalOrderId}/resend-booking-confirmation`;
  return await request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(payload),
  });
}

// BUSINESS SUMMARY

type GetBusinessSummaryResponse =
  paths['/api/business-traveller/businesses/{businessId}/business-summary']['get']['responses'][200]['content']['application/json'];

type UpdateBusinessSummaryResponse =
  paths['/api/business-traveller/businesses/{businessId}/business-summary']['put']['responses'][200]['content']['application/json'];

type UpdateBusinessSummaryPayload =
  paths['/api/business-traveller/businesses/{businessId}/business-summary']['put']['requestBody']['content']['application/json'];

export async function getBusinessSummary(businessId: string): Promise<GetBusinessSummaryResponse> {
  const url = `${businessTravellerBasePath()}/businesses/${businessId}/business-summary`;
  return await request(url, { method: 'GET' });
}

export async function updateBusinessSummary(
  businessId: string,
  payload: UpdateBusinessSummaryPayload,
): Promise<UpdateBusinessSummaryResponse> {
  const url = `${businessTravellerBasePath()}/businesses/${businessId}/business-summary`;
  return await request(url, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(payload),
  });
}

// Admin refund proxy: routes refund through svc-business so it can inject business_credit_refund_amount
export type BusinessTravellerProxyAdminRefundType = 'admin_item_refund' | 'admin_flight_refund';

export async function businessTravellerProxyAdminRefund(
  businessId: string,
  orderId: string,
  orderItemId: string,
  type: BusinessTravellerProxyAdminRefundType,
  details: Record<string, unknown>,
) {
  const url = `${businessTravellerBasePath()}/businesses/${businessId}/orders/${orderId}/order-items/${orderItemId}/admin-refund`;
  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify({ type, details }),
  });
}

// GET admin refund preview proxy: routes through svc-business so it can inject business_credit_refund_amount
export async function businessTravellerProxyGetAdminRefund(
  businessId: string,
  orderId: string,
  orderItemId: string,
  queryParams: string,
) {
  const url = `${businessTravellerBasePath()}/businesses/${businessId}/orders/${orderId}/order-items/${orderItemId}/admin-refund?${queryParams}`;
  return request(url);
}

// Get activity log for order checkout fields
export type OrderCheckoutFieldActivityLogResponse =
  paths['/api/business-traveller/businesses/{businessId}/orders/{externalOrderId}/checkout-fields/activity']['get']['responses'][200]['content']['application/json'];

export async function getOrderCheckoutFieldActivityLog(
  businessId: string,
  externalOrderId: string,
): Promise<OrderCheckoutFieldActivityLogResponse> {
  const url = `${businessTravellerBasePath()}/businesses/${businessId}/orders/${externalOrderId}/checkout-fields/activity`;
  return await request(url, { method: 'GET' });
}

// NDC PREFERENCE REQUESTS

type NdcPreferenceListResponse =
  paths['/api/business-traveller/businesses/{businessId}/ndc-preferences']['get']['responses'][200]['content']['application/json'];

type NdcSupportedCarriersResponse =
  paths['/api/business-traveller/ndc-preferences/supported-carriers']['get']['responses'][200]['content']['application/json'];

export type BusinessNdcPreference = NdcPreferenceListResponse['result'][number];

export type NdcSupportedCarrier = NdcSupportedCarriersResponse['result'][number];

type NdcPreferenceCreateBody =
  paths['/api/business-traveller/businesses/{businessId}/ndc-preferences']['post']['requestBody']['content']['application/json'];

// Widen carrierCode to string since carriers are fetched dynamically at runtime;
export type NdcPreferenceCreatePayload = Omit<NdcPreferenceCreateBody, 'carrierCode'> & {
  carrierCode: string;
};

export async function getNdcSupportedCarriers(): Promise<Array<NdcSupportedCarrier>> {
  const url = `${businessTravellerBasePath()}/ndc-preferences/supported-carriers`;
  const response: NdcSupportedCarriersResponse = await request(url, { method: 'GET' });
  return response.result;
}

export async function getNdcPreferences(businessId: string): Promise<Array<BusinessNdcPreference>> {
  const url = `${businessTravellerBasePath()}/businesses/${businessId}/ndc-preferences`;
  const response: NdcPreferenceListResponse = await request(url, { method: 'GET' });
  return response.result;
}

export async function createNdcPreference(businessId: string, payload: NdcPreferenceCreatePayload) {
  const url = `${businessTravellerBasePath()}/businesses/${businessId}/ndc-preferences`;
  return await request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(payload),
  });
}

export async function updateNdcPreference(businessId: string, ndcPreferenceId: string, payload: { enabled: boolean }) {
  const url = `${businessTravellerBasePath()}/businesses/${businessId}/ndc-preferences/${ndcPreferenceId}`;
  return await request(url, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(payload),
  });
}
